import axios from 'axios';
import { httpClient } from '../../api/httpClient';
import { AuthMeResponse, AuthMode, AuthResponse, AuthSession, ForgotPasswordResponse, ResetPasswordResponse, TelegramMigrationTokenResponse } from './auth.schemas';
import { getDeviceId } from './auth.storage';

export function extractSessionToken(data: AuthResponse) {
  return data.session_token || data.sessionToken || data.access_token || data.token;
}

export async function authenticateByEmail(mode: AuthMode, email: string, password: string): Promise<AuthSession> {
  const endpoint = mode === 'register' ? '/v2/auth/email/register' : '/v2/auth/email/login';
  const response = await httpClient.post<AuthResponse>(endpoint, {
    email,
    password,
    platform: 'web',
    device_id: getDeviceId(),
  });

  const sessionToken = extractSessionToken(response.data);

  if (!sessionToken) {
    throw new Error('Сервер не вернул session_token');
  }

  return {
    email,
    sessionToken,
  };
}

export async function setEmailPassword(password: string, currentPassword?: string) {
  await httpClient.post('/v2/auth/email/password/set', {
    password,
    current_password: currentPassword || undefined,
  });
}

export async function changeEmailPassword(currentPassword: string, newPassword: string) {
  await httpClient.post('/v2/auth/email/password/change', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

export async function forgotEmailPassword(email: string) {
  const response = await httpClient.post<ForgotPasswordResponse>('/v2/auth/email/password/forgot', {
    email,
  });

  return response.data;
}

export async function resetEmailPassword(token: string, newPassword: string) {
  const response = await httpClient.post<ResetPasswordResponse>('/v2/auth/email/password/reset', {
    token,
    new_password: newPassword,
  });

  return response.data;
}

export async function getAuthMe() {
  const response = await httpClient.get<AuthMeResponse>('/v2/auth/me');
  return response.data;
}

export async function claimReferral(code: string) {
  await httpClient.post('/v2/me/ref/claim', { code });
}

export async function acceptTerms() {
  await httpClient.post('/v2/me/terms/accept');
}

export async function createTelegramMigrationToken(initData: string) {
  const response = await httpClient.post<TelegramMigrationTokenResponse>('/v2/auth/telegram/migration-token', {
    initData,
  });

  return response.data;
}

export async function importTelegramAccount(migrationToken: string) {
  const response = await httpClient.post<AuthResponse>('/v2/auth/telegram/import', {
    migration_token: migrationToken,
    platform: 'web',
  });

  const sessionToken = extractSessionToken(response.data);

  if (!sessionToken) {
    throw new Error('Сервер не вернул новый session_token');
  }

  return {
    data: response.data,
    sessionToken,
  };
}

export function getAuthErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const authErrorMessage = 'Неправильный логин или пароль';

    if (typeof data === 'string') {
      if (data.toLowerCase().includes('invalid email or password')) {
        return authErrorMessage;
      }

      return data;
    }

    if (data && typeof data === 'object') {
      const message =
        'message' in data && typeof data.message === 'string'
          ? data.message
          : 'detail' in data && typeof data.detail === 'string'
            ? data.detail
            : null;

      if (message) {
        if (message.toLowerCase().includes('invalid email or password')) {
          return authErrorMessage;
        }

        if (message.toLowerCase().includes('invalid or expired reset token')) {
          return 'Ссылка для восстановления некорректна или устарела';
        }

        return message;
      }
    }

    if (error.response?.status === 401) {
      return authErrorMessage;
    }
  }

  return error instanceof Error ? error.message : 'Не удалось выполнить запрос';
}
